import moment from 'moment';
import _ from 'lodash';
import { CellRenderers } from 'utils/tableUtils';
import { FINDING_STATUS_DISPLAY_TEXT } from 'views/Findings/Constants';

export const startCase = (text) => CellRenderers.VALUE_OR_NA(_.startCase(_.toLower(text)));
export const capitalize = (text) => CellRenderers.VALUE_OR_NA(_.capitalize(_.lowerCase(text)));

export const findingStatusFormatter = (text) => (FINDING_STATUS_DISPLAY_TEXT[text] ?? text);

export const appendDays = (day) => {
    let suffix = 'Day';
    if(Number(day) >= 0){
        suffix = 'Days';
    }
    return `${day} ${suffix}`;
}

/**
 * 
 * @param {String} cronString
 * @returns {Object} { time: moment Object, daysOfTheWeek: integer Array with Sunday being 0 and Saturday being 6}
 * ref: http://www.quartz-scheduler.org/documentation/quartz-2.3.0/tutorials/crontrigger.html
 */
 export const getConfigFromCronString = (cronString) => {
    if(!_.isString(cronString)){
        return {};
    }
    const cronAllowedLength = 6;
    const cronConfig = cronString.split(' ');
    if(!(Array.isArray(cronConfig)) || cronConfig.length !== cronAllowedLength){
        return {};
    }
    const [second, minute, hour] = cronConfig;
    let daysOfTheWeek = cronConfig[cronAllowedLength-1];
    if(!_.isString(daysOfTheWeek) || daysOfTheWeek === '?'){
        daysOfTheWeek = [];
    }else if(daysOfTheWeek === '*'){
        daysOfTheWeek = [1,2,3,4,5,6,7];
    }else{
        daysOfTheWeek = daysOfTheWeek.split(',').map(day => Number(day));
    }
    const time = moment(`${hour}:${minute}:${second}`, 'HH:mm:ss');
    return {
        time,
        daysOfTheWeek
    }
}

/**
 * 
 * @param {Object} { time: moment Object, daysOfTheWeek: integer Array with Sunday being 0 and Saturday being 6}
 * @returns {String} cron: cron string 
 * ref: http://www.quartz-scheduler.org/documentation/quartz-2.3.0/tutorials/crontrigger.html
 */
export const getCronStringFromConfig = ({ time, daysOfTheWeek }) => {
    if(!moment.isMoment(time) || !Array.isArray(daysOfTheWeek) || !daysOfTheWeek.length){
        return;
    }
    const second = 0;
    const minute = time.get('minute');
    const hour = time.get('hour');
    const dayOfTheMonth = '?';
    const month = '*';
    let daysOfTheWeekString = daysOfTheWeek.join(',');
    return `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${daysOfTheWeekString}`;
}

/**
 * 
 * @param {String} cronString 
 * @returns {String} summary string formatted as hh:mm a, d1/d2/d3 
 * e.g. 06:05 am, M/T/T/F 
 */
export const getFormattedCronSummaryString = (cronString, stringResult = true) => {
    const {
        time,
        daysOfTheWeek
    } = getConfigFromCronString(cronString);
    if(!moment.isMoment(time) || !Array.isArray(daysOfTheWeek) || !daysOfTheWeek.length){
        return
    }
    const dayShortHands = {
        1: 'Sun',
        2: 'Mon',
        3: 'Tue',
        4: 'Wed',
        5: 'Thu',
        6: 'Fri',
        7: 'Sat'
    }
    const weekDaysRefString = '23456';
    const weekEndRefString = '17';
    let dayString;
    let isOddDay = false;
    const daysOfTheWeekString = daysOfTheWeek.sort().join``;
    if(daysOfTheWeekString.length === Object.keys(dayShortHands).length){
        dayString = 'Everyday'
    } else if(daysOfTheWeekString === weekDaysRefString){
        dayString = 'Every Weekday'
    }else if(daysOfTheWeekString === weekEndRefString){
       dayString = 'Every Weekend'
    }
    else{
        isOddDay = true;
        dayString = stringResult ? `Every ${daysOfTheWeek.sort().map(day => (dayShortHands[day])).join('/')}`: daysOfTheWeek.sort().map(day => (dayShortHands[day]));
    }
    
    const formattedTime = time.format('hh:mm a');

    return (stringResult || !isOddDay) ? `${formattedTime}, ${dayString}`: { formattedTime, dayString };
}

export const convertCronString = ({ cron, displayInTimeZone = false }) => {
    if (displayInTimeZone) {
        return convertCronToLocalTimeZone(cron);
    } else {
        return convertCronToUTC(cron);
    }
}

/**
 * 
 * @param {*} cron 
 * @returns cron
 * parsing the entire cron along with days
 * Eg: IST Fri 2:00am should be Thu 8:30pm UTC
 */
function convertCronToLocalTimeZone(cron){
    const { time, daysOfTheWeek } = getConfigFromCronString(cron);
    const utcOffset = time.utcOffset();

    const localDaysOfTheWeek = _.map(daysOfTheWeek, dayInUTC => {
        // dayInUTC starts from 1, but moment considers from 0
        const utcDate = moment(time).day(dayInUTC - 1).add(utcOffset, "minutes");
        return utcDate.day() + 1;
    });

    const localTime = moment(time).add(utcOffset, "minutes");

    return getCronStringFromConfig({ time: localTime, daysOfTheWeek: localDaysOfTheWeek });
}


/**
 * 
 * @param {*} cron 
 * @returns cron
 * parsing the entire cron along with days
 * Eg: UTC Thu 8:30pm should be Fri 2:00am IST
 */
function convertCronToUTC(cron){
    const { time, daysOfTheWeek } = getConfigFromCronString(cron);
    const utcOffset = time.utcOffset();

    const UTCDaysOfTheWeek = _.map(daysOfTheWeek, dayInUTC => {
        // dayInUTC starts from 1, but moment considers from 0
        const localDate = moment(time).day(dayInUTC - 1).subtract(utcOffset, "minutes");
        return localDate.day() + 1;
    });

    const UTCTime = moment(time).subtract(utcOffset, "minutes");

    return getCronStringFromConfig({ time: UTCTime, daysOfTheWeek: UTCDaysOfTheWeek });
}
